import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { APPostingApprovalModal } from '@components/APPosting/APPostingApprovalModal';
import { Button } from '@components/common/Button';
import { Input } from '@components/common/Input';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { TableViewControls } from '@components/common/TableViewControls';
import PlanGate from '@components/common/PlanGate';
import UpgradeBanner from '@components/common/UpgradeBanner';
import { TableViewLayout } from '@components/common/TableViewLayout';
import { tableHeaderCellClass, tableHeaderRowClass } from '@components/common/tableViewStyles';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { useAPPostings } from '@api/hooks/useAPPostings';
import {
  useCreatePaymentOrder,
  usePaymentOrders,
  useStripeConnection,
  useSubmitPaymentOrder,
  useVendorPaymentProfiles,
} from '@api/hooks/usePayments';
import { usePlanFeature } from '@api/hooks/useSubscription';
import type { APPosting } from '@schemas/api';
import { formatDate, formatDateTime } from '@utils/formatters';

export const APPostingsPage = ({ approvalOnly = false, hideHeader = false }: { approvalOnly?: boolean; hideHeader?: boolean }) => {
  return (
    <PlanGate feature="ap_postings" fallback={<UpgradeBanner feature="ap_postings" />}>
      <APPostingsContent approvalOnly={approvalOnly} hideHeader={hideHeader} />
    </PlanGate>
  );
};

const APPostingsContent = ({ approvalOnly, hideHeader }: { approvalOnly: boolean; hideHeader: boolean }) => {
  const [params, setParams] = useSearchParams();
  const linkedPostingId = params.get('posting_id') ?? undefined;
  const openedPosting = useRef<string>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'payment_created' | 'ready' | 'attention'>('all');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [sortBy, setSortBy] = useState<'created_at' | 'invoice' | 'vendor' | 'invoice_total' | 'amount' | 'discount' | 'status' | 'payment'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedPosting, setSelectedPosting] = useState<APPosting | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentPosting, setPaymentPosting] = useState<APPosting | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const paymentFeature = usePlanFeature('payments');
  const canUsePayments = paymentFeature && !approvalOnly;

  const { data: postings, isLoading, error } = useAPPostings(linkedPostingId ? undefined : approvalOnly ? 'pending_approval' : statusFilter, linkedPostingId);
  const { data: paymentOrders = [] } = usePaymentOrders('ap_posting', canUsePayments);
  const { data: stripeConnection } = useStripeConnection(canUsePayments);
  const { data: vendorProfiles = [] } = useVendorPaymentProfiles(canUsePayments);
  useEffect(() => {
    // The server applies the exact reference filter; legacy responses may use UUID IDs.
    const posting = linkedPostingId ? postings?.[0] : undefined;
    if (posting && openedPosting.current !== linkedPostingId) {
      openedPosting.current = linkedPostingId;
      setSelectedPosting(posting);
      setIsModalOpen(true);
    }
  }, [postings, linkedPostingId]);
  const createPaymentOrder = useCreatePaymentOrder();
  const submitPaymentOrder = useSubmitPaymentOrder();

  const handleApprove = (posting: APPosting) => {
    setSelectedPosting(posting);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedPosting(null);
    setIsModalOpen(false);
    if (linkedPostingId) {
      const next = new URLSearchParams(params);
      next.delete('posting_id');
      setParams(next, { replace: true });
      openedPosting.current = undefined;
    }
  };

  const paymentOrdersByPostingId = useMemo(
    () =>
      paymentOrders.reduce<Record<string, (typeof paymentOrders)[number]>>((acc, order) => {
        if (order.ap_posting_id) {
          acc[order.ap_posting_id] = order;
        }
        return acc;
      }, {}),
    [paymentOrders],
  );

  const vendorProfilesByVendorId = useMemo(
    () =>
      vendorProfiles.reduce<Record<string, (typeof vendorProfiles)[number]>>((acc, profile) => {
        acc[profile.vendor_id] = profile;
        return acc;
      }, {}),
    [vendorProfiles],
  );

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'approved', label: 'Approved' },
    { value: 'posted', label: 'Posted' },
    { value: 'discarded', label: 'Discarded' },
  ];

  const paymentStateFor = (posting: APPosting) => {
    if (paymentOrdersByPostingId[posting.id]) return 'payment_created';
    const vendorProfile = posting.vendor_id ? vendorProfilesByVendorId[posting.vendor_id] : undefined;
    if (
      stripeConnection?.status === 'ready'
      && canUsePayments
      && vendorProfile?.status === 'ready'
    ) return 'ready';
    return 'attention';
  };

  const visiblePostings = useMemo(() => {
    const valueFor = (posting: APPosting) => {
      if (sortBy === 'invoice') return posting.invoice_num || '';
      if (sortBy === 'vendor') return posting.vendor_name || posting.vendor_name_extracted || '';
      if (sortBy === 'invoice_total') return posting.invoice_total;
      if (sortBy === 'amount') return posting.posting_amount;
      if (sortBy === 'discount') return posting.discount_info?.discount_percent ?? 0;
      if (sortBy === 'status') return posting.status;
      if (sortBy === 'payment') return paymentStateFor(posting);
      return new Date(posting.created_at).getTime();
    };

    const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
    const minAmount = Number(amountMin);
    const maxAmount = Number(amountMax);

    return [...(postings ?? [])]
      .filter((posting) => {
        if (normalizedSearch) {
          const searchable = [
            posting.invoice_num,
            posting.vendor_name,
            posting.vendor_name_extracted,
            posting.erp_reference,
          ].filter(Boolean).join(' ').toLocaleLowerCase();
          if (!searchable.includes(normalizedSearch)) return false;
        }
        if (paymentFilter !== 'all' && paymentStateFor(posting) !== paymentFilter) return false;
        if (amountMin && (Number.isNaN(minAmount) || posting.posting_amount < minAmount)) return false;
        if (amountMax && (Number.isNaN(maxAmount) || posting.posting_amount > maxAmount)) return false;
        const createdDate = posting.created_at.slice(0, 10);
        if (createdFrom && createdDate < createdFrom) return false;
        if (createdTo && createdDate > createdTo) return false;
        return true;
      })
      .sort((left, right) => {
      const leftValue = valueFor(left);
      const rightValue = valueFor(right);
      const comparison = typeof leftValue === 'string'
        ? leftValue.localeCompare(String(rightValue))
        : Number(leftValue) - Number(rightValue);
      return sortOrder === 'asc' ? comparison : -comparison;
      });
  }, [
    amountMax,
    amountMin,
    canUsePayments,
    createdFrom,
    createdTo,
    paymentFilter,
    paymentOrdersByPostingId,
    postings,
    searchTerm,
    sortBy,
    sortOrder,
    stripeConnection?.status,
    vendorProfilesByVendorId,
  ]);

  const formatCurrency = (value: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending_approval':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'approved':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'posted':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'discarded':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'expired':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const handleCreatePayment = async (releaseMode: 'manual' | 'scheduled') => {
    if (!paymentPosting) return;
    const order = await createPaymentOrder.mutateAsync({
      source_type: 'ap_posting',
      source_id: paymentPosting.id,
      amount: paymentPosting.posting_amount.toFixed(2),
      currency: paymentPosting.currency,
      release_mode: releaseMode,
      scheduled_for: releaseMode === 'scheduled' && scheduleDate ? new Date(`${scheduleDate}T12:00:00`).toISOString() : null,
      remittance_note: `AP posting ${paymentPosting.invoice_num}`,
    });

    if (releaseMode === 'manual') {
      await submitPaymentOrder.mutateAsync(order.id);
    }

    setPaymentPosting(null);
    setScheduleDate('');
  };

  const canSubmitExistingPaymentOrder = (status: string) =>
    status === 'draft' || status === 'scheduled' || status === 'failed';

  const handleSubmitExistingPaymentOrder = async (paymentOrderId: string) => {
    await submitPaymentOrder.mutateAsync(paymentOrderId);
  };

  return (
    <div className="space-y-6">
      {!hideHeader && <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50">AP Postings</h1>}

      <TableViewLayout
        title="Postings"
        summary={visiblePostings.length ? `Showing 1-${visiblePostings.length} of ${visiblePostings.length}` : "Showing 0-0 of 0"}
        controls={
          <TableViewControls
            activeFilterCount={[
              statusFilter,
              paymentFilter !== 'all',
              amountMin,
              amountMax,
              createdFrom,
              createdTo,
            ].filter(Boolean).length}
            onClearFilters={() => {
              setStatusFilter(undefined);
              setPaymentFilter('all');
              setAmountMin('');
              setAmountMax('');
              setCreatedFrom('');
              setCreatedTo('');
            }}
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchLabel="Search AP postings"
            searchPlaceholder="Search invoice, vendor, or ERP reference…"
            filterContent={
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                  <SearchableSelect
                    aria-label="Posting status"
                    options={statusOptions.map((option) => ({ id: option.value, label: option.label }))}
                    value={approvalOnly ? 'pending_approval' : statusFilter || ''}
                    disabled={approvalOnly}
                    onChange={(value) => setStatusFilter(value || undefined)}
                    clearable={false}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Payment readiness</label>
                  <SearchableSelect
                    aria-label="Posting payment readiness"
                    options={[
                      { id: 'all', label: 'All payment states' },
                      { id: 'payment_created', label: 'Payment created' },
                      { id: 'ready', label: 'Ready to pay' },
                      { id: 'attention', label: 'Needs setup or attention' },
                    ]}
                    value={paymentFilter}
                    onChange={(value) => setPaymentFilter(value as typeof paymentFilter)}
                    clearable={false}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Minimum amount
                    <Input type="number" min="0" step="0.01" value={amountMin} onChange={(event) => setAmountMin(event.target.value)} className="mt-2" />
                  </label>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Maximum amount
                    <Input type="number" min="0" step="0.01" value={amountMax} onChange={(event) => setAmountMax(event.target.value)} className="mt-2" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Created from
                    <Input type="date" value={createdFrom} onChange={(event) => setCreatedFrom(event.target.value)} className="mt-2" />
                  </label>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Created to
                    <Input type="date" value={createdTo} onChange={(event) => setCreatedTo(event.target.value)} className="mt-2" />
                  </label>
                </div>
              </div>
            }
            sortContent={
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Sort by</label>
                  <SearchableSelect
                    aria-label="Sort postings by"
                    options={[
                      { id: 'created_at', label: 'Created date' },
                      { id: 'invoice', label: 'Invoice ID' },
                      { id: 'vendor', label: 'Vendor' },
                      { id: 'invoice_total', label: 'Invoice total' },
                      { id: 'amount', label: 'Posting amount' },
                      { id: 'discount', label: 'Discount' },
                      { id: 'status', label: 'Posting status' },
                      { id: 'payment', label: 'Payment readiness' },
                    ]}
                    value={sortBy}
                    onChange={(value) => setSortBy(value as typeof sortBy)}
                    clearable={false}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Direction</label>
                  <SearchableSelect
                    aria-label="Posting sort direction"
                    options={[{ id: 'desc', label: 'Descending' }, { id: 'asc', label: 'Ascending' }]}
                    value={sortOrder}
                    onChange={(value) => setSortOrder(value as typeof sortOrder)}
                    clearable={false}
                  />
                </div>
              </div>
            }
          />
        }
      >
          {isLoading ? (
            <div className="flex justify-center items-center h-96">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md p-4 text-red-800 dark:text-red-200">
              Failed to load AP postings. Please try again.
            </div>
          ) : visiblePostings.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p className="text-lg">No AP postings found</p>
              <p className="text-sm">All invoices are either matched or posted</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={tableHeaderRowClass}>
                  <tr>
                    <th className={`${tableHeaderCellClass} text-left`}>Invoice ID</th>
                    <th className={`${tableHeaderCellClass} text-left`}>Vendor</th>
                    <th className={`${tableHeaderCellClass} text-right`}>Invoice Total</th>
                    <th className={`${tableHeaderCellClass} text-right`}>Posting Amount</th>
                    <th className={`${tableHeaderCellClass} text-center`}>Discount</th>
                    <th className={`${tableHeaderCellClass} text-center`}>Status</th>
                    <th className={`${tableHeaderCellClass} text-center`}>Payment</th>
                    <th className={`${tableHeaderCellClass} text-center`}>Created</th>
                    <th className={`${tableHeaderCellClass} text-center`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePostings.map((posting) => {
                    const paymentOrder = paymentOrdersByPostingId[posting.id];
                    const vendorProfile = posting.vendor_id ? vendorProfilesByVendorId[posting.vendor_id] : undefined;

                    return (
                      <tr
                        key={posting.id}
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-900 dark:text-gray-100 font-medium">{posting.invoice_num}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{posting.vendor_name || posting.vendor_name_extracted || '-'}</td>
                        <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatCurrency(posting.invoice_total, posting.currency)}</td>
                        <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100 font-medium">{formatCurrency(posting.posting_amount, posting.currency)}</td>
                        <td className="px-4 py-3 text-center">
                          {posting.discount_info?.has_discount ? (
                            <div className="text-sm">
                              <div className="text-blue-600 dark:text-blue-400 font-medium">{posting.discount_info.discount_percent}%</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                by {formatDate(posting.discount_info.discount_deadline)}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-gray-600">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(posting.status)}`}>
                            {posting.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {paymentOrder ? (
                            <span className="inline-block rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                              {paymentOrder.status.replace('_', ' ')}
                      </span>
                          ) : !stripeConnection || stripeConnection.status !== 'ready' ? (
                            <span className="text-xs text-amber-700">Stripe not ready</span>
                          ) : !canUsePayments ? (
                            <span className="text-xs text-amber-700">Payment plan required</span>
                          ) : !vendorProfile || vendorProfile.status !== 'ready' ? (
                            <span className="text-xs text-amber-700">Vendor setup needed</span>
                          ) : (
                            <span className="text-xs text-gray-500">Ready</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400 text-xs">{formatDateTime(posting.created_at)}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {posting.status === 'pending_approval' && (
                              <Button size="sm" onClick={() => handleApprove(posting)} className="whitespace-nowrap">
                                Review
                              </Button>
                            )}
                            {(posting.status === 'posted' || posting.status === 'posting_failed')
                              && posting.erp_reference && (
                              <Button size="sm" variant="secondary" onClick={() => handleApprove(posting)} className="whitespace-nowrap">
                                Correct
                              </Button>
                            )}
                            {paymentOrder && canSubmitExistingPaymentOrder(paymentOrder.status) && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleSubmitExistingPaymentOrder(paymentOrder.id)}
                                disabled={!canUsePayments || submitPaymentOrder.isPending}
                                className="whitespace-nowrap"
                              >
                                {submitPaymentOrder.isPending
                                  ? 'Submitting…'
                                  : paymentOrder.status === 'scheduled'
                                    ? 'Submit Now'
                                    : 'Submit Payment'}
                              </Button>
                            )}
                            {canUsePayments && (posting.status === 'approved' || posting.status === 'posted') && !paymentOrder && (
                              <Button size="sm" variant="secondary" onClick={() => setPaymentPosting(posting)} className="whitespace-nowrap">
                                Pay
                              </Button>
                            )}
                            {posting.status !== 'pending_approval' &&
                              !(posting.status === 'approved' || posting.status === 'posted') &&
                              !paymentOrder && (
                              <span className="text-gray-400 dark:text-gray-600 text-xs">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </TableViewLayout>

      {isModalOpen && selectedPosting && (
        <APPostingApprovalModal posting={selectedPosting} isOpen={isModalOpen} onClose={handleCloseModal} />
      )}

      {paymentPosting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-surface-base-dark">
            <div className="space-y-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create Stripe Payment</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {paymentPosting.vendor_name || paymentPosting.vendor_name_extracted || 'Vendor'} • {formatCurrency(paymentPosting.posting_amount, paymentPosting.currency)}
              </p>
            </div>

            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
              <p>Tenant Stripe status: {stripeConnection?.status?.replace(/_/g, ' ') || 'not started'}</p>
              <p>Vendor payout status: {(paymentPosting.vendor_id && vendorProfilesByVendorId[paymentPosting.vendor_id]?.status) || 'not started'}</p>
            </div>

            <label className="mt-4 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Schedule Date
              <input
                type="date"
                value={scheduleDate}
                onChange={(event) => setScheduleDate(event.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
              />
            </label>

            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => { setPaymentPosting(null); setScheduleDate(''); }}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleCreatePayment('scheduled')}
                disabled={!scheduleDate || createPaymentOrder.isPending || submitPaymentOrder.isPending}
              >
                Schedule
              </Button>
              <Button
                type="button"
                onClick={() => handleCreatePayment('manual')}
                disabled={createPaymentOrder.isPending || submitPaymentOrder.isPending}
              >
                Pay Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
